/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { AbstractControl, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { BaseComponent } from '../../../../../../core/components';
import { Component } from '@angular/core';
import { ErrorResponseModel } from '../../../../../../core/models';
import { GslbApiService } from '../../../../shared/http';
import { GslbFrontendModel } from '../../../../shared/models';
import { LbErrorService } from '../../../../shared/services/components';
import {
    LanguageService,
    LoadingService,
    LogService,
    NavigationService,
    UtilsService
} from '../../../../../../core/services';
import { Observable, Subscriber } from 'rxjs';
import { globalNamesDomainValidation } from '../../../../shared/directives/validation';
import { takeUntil } from 'rxjs/operators';

@Component({
    selector: 'app-add-edit-global-names',
    templateUrl: './addEditGlobalNames.component.html'
})

export class AddEditGlobalNamesComponent extends BaseComponent {
    /**
     * Global names form group.
     */
    public globalNameForm: FormGroup;

    /**
     * Our available made global names from the API.
     */
    public globalNames: GslbFrontendModel[] = [];

    /**
     * To store the edit global name.
     * @private
     */
    private currentGlobalName: GslbFrontendModel;

    /**
     * To store the available global names.
     * @private
     */
    private availableGlobalNames: any[] = [];

    /**
     * Store the edit id.
     * @private
     */
    public editId: string;

    /**
     * Store the input width for time to live.
     */
    public inputWidth: number = 83;

    /**
     * Store initial form data to compare against any changes.
     * @private
     */
    private initialData: any = {};

    constructor(
        private formBuilder: FormBuilder,
        private gslbApiService: GslbApiService,
        private languageService: LanguageService,
        private lbErrorService: LbErrorService,
        private loadingService: LoadingService,
        private logService: LogService,
        private navigationService: NavigationService,
        private route: ActivatedRoute,
        private router: Router,
        private utilsService: UtilsService
    ) {
        super();

        this.setUpGlobalNames();
    }

    /**
     * Save the global name.
     */
    public submitGlobalName(): Observable<any> {
        return new Observable((observer: Subscriber<any>): void => {
            const apiCall = this.getApiCall();
            apiCall
                .pipe(takeUntil(this.destroyed$))
                .subscribe({
                    next: (): void => {
                        this.navigateToURL('/cluster-configuration/gslb?sFilter=global-names');
                        observer.next();
                        observer.complete();
                    },
                    error: (error: ErrorResponseModel): void => {
                        this.lbErrorService.pageWithErrors(this.globalNameForm, error.error.errors);
                        this.logService.error('GslbComponent: submitGlobalName', 'Error submitting global name', error);
                        this.loadingService.loadingButtonEmitter.next(false);
                        observer.error(error);
                        observer.complete();
                    },
                    complete: (): void => {
                        observer.next();
                        observer.complete();
                    }
                });
        });
    }

    /**
     * Get the global name data for submitting.
     * @private
     */
    private getApiCall(): any {
        const globalName: any = this.globalNameForm.getRawValue();
        const apiCall = this.editId
            ? this.gslbApiService.updateFrontend(globalName)
            : this.gslbApiService.addFrontend(globalName);

        if (this.editId) {
            globalName.id = this.currentGlobalName.id;
        }

        return apiCall;
    }

    /**
     * Set up the global name's data.
     * @private
     */
    private setUpGlobalNames(): void {
        this.editId = this.route.snapshot.params['id'];
        this.setUpGlobalNamesForm();
        this.getGlobalNamesData();
        this.setUpGlobalNamesSubscribers();
    }

    /**
     * Get the global names from the API.
     * @private
     */
    private getGlobalNamesData(): void {
        this.gslbApiService.getFrontends()
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (globalNames: any[]): void => {
                    this.globalNames = globalNames;
                    this.availableGlobalNames = this.globalNames;
                    if (this.editId) {
                        this.patchDataToForm();
                    }
                }
            });
    }

    /**
     * Set up the edit data.
     * @private
     */
    private patchDataToForm(): void {
        this.currentGlobalName = this.globalNames.find((item: GslbFrontendModel): boolean => item.id === this.editId);
        this.globalNameForm.get('domain').setValue(this.currentGlobalName.domain);
        this.globalNameForm.get('timeToLive').setValue(this.currentGlobalName.timeToLive);
        this.initialData = this.globalNameForm.value;
    }

    /**
     * Used to set up the global names form.
     * @private
     */
    private setUpGlobalNamesForm(): void {
        this.globalNameForm = this.formBuilder.group({
            domain: [null, [Validators.required, globalNamesDomainValidation]],
            timeToLive: [null, [Validators.required, Validators.min(0)]],
        });
    }

    /**
     * Set up the subscribers.
     * @private
     */
    private setUpGlobalNamesSubscribers(): void {
        this.globalNameForm.valueChanges
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: form => {
                    this.utilsService.debounce('form', (): void => {
                        if (form['domain'] !== null) {
                            this.validateDomainName(form['domain']);
                        }

                        if (this.editId && !this.utilsService.hasFormDataChanged(this.initialData, form)) {
                            this.globalNameForm.markAsPristine();
                        }
                    }, 250);
                }
            });

        this.globalNameForm.get('timeToLive').valueChanges
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: value => {
                    this.utilsService.debounce('timeToLive', (): void => {
                        if (value) {
                            this.resizeInput(value);
                        }
                    }, 250);
                }
            });
    }

    /**
     * Resize time to live input.
     * @param value
     * @private
     */
    private resizeInput(value: string | number): void {
        const valToString: string = value.toString();
        if (valToString.length < 5) {
            this.inputWidth = 83;
        } else if (valToString.length >= 5 && valToString.length <= 8) {
            this.inputWidth = 108;
        } else {
            this.inputWidth = 130;
        }
    }

    /**
     * Check the domain name to ensure it's not a duplicate value.
     * @param name
     * @private
     */
    private validateDomainName(name: string): void {
        const domainName: AbstractControl = this.globalNameForm.get('domain');
        let duplicateGlobalName = this.availableGlobalNames.find(globalName => globalName.domain === name);
        if (this.editId && duplicateGlobalName && duplicateGlobalName.id === this.currentGlobalName.id) {
            duplicateGlobalName = undefined;
        }

        if (duplicateGlobalName) {
            domainName.setErrors({ 'duplicateLabel': `${this.languageService.translate('duplicate_global_name')}` });
        }
    }

    /**
     * Navigate to supplied URL.
     * @param url
     * @private
     */
    private navigateToURL(url: string): void {
        this.router.navigateByUrl(this.navigationService.getEnduranceUrl(url)).then().catch();
    }
}
